import { useEffect, useState } from 'react';
import { Modal } from '../../../components/modal/modal';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Field } from '../../../components/field/field';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { ProcessingFeeOverride, ProcessingFeeOverrideConf, User } from '../../../clients/quipli-service/user/user';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { toast } from '../../../util/toasts';
import { ButtonGroup } from '../../../components/button-group/button-group';
import { Phone } from '../../../components/field/phone';
import { UpsertRepresentativeRequest } from '../../../clients/quipli-service/representatives/representative';

export const UserForm = ({
  isOpen,
  user,
  close,
  setNewUser,
}: {
  isOpen: boolean;
  user?: User;
  close: () => any;
  setNewUser?: (u: User) => void;
}) => {
  const { createUser, updateUser, getConfiguration } = useAPI();

  const [name, setName] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [processingFeeOverride, setProcessingFeeOverride] = useState<ProcessingFeeOverride>();
  const [processingFeeOverrideConf, setProcessingFeeOverrideConf] = useState<ProcessingFeeOverrideConf>();
  const [withoutEmailChecked, setWithoutEmailChecked] = useState<boolean>(false);
  const [userType, setUserType] = useState<string>(user?.structureType ?? 'user');
  const [representativeName, setRepresentativeName] = useState<string>();
  const [representativePhone, setRepresentativePhone] = useState<string>('');
  const [representativePhoneIsValid, setRepresentativePhoneIsValid] = useState<boolean>(false);
  const [representativeTitle, setRepresentativeTitle] = useState<string>();
  const [representativeEmail, setRepresentativeEmail] = useState<string>();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const clearValues = () => {
    setName('');
    setCompany('');
    setPhone('');
    setEmail('');
    setWithoutEmailChecked(false);
    setUserType('user');
    setProcessingFeeOverride(undefined);
    close();
  };

  const onSubmit = async () => {
    if (isDisabled) return;

    if (phone !== '' && !phoneIsValid) {
      toast('Please enter a valid phone number', 'error');
      return;
    }

    if (representativePhone !== '' && !representativePhoneIsValid) {
      toast('Please enter a valid representative phone number', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const representatives: Omit<UpsertRepresentativeRequest, 'userId'>[] | undefined =
        userType == 'company' && representativeName
          ? [
              {
                name: representativeName,
                phone: representativePhone,
                title: representativeTitle,
                email:
                  representativeEmail && representativeEmail.length > 0 ? representativeEmail : 'customer@quipli.com',
              },
            ]
          : undefined;
      const newUser = await (user
        ? updateUser({ ...user, name, email: email.trim(), company, phone, processingFeeOverride })
        : createUser(
            name,
            withoutEmailChecked,
            userType,
            email.trim(),
            company,
            phone,
            representatives,
            processingFeeOverride,
          ));
      setNewUser && setNewUser(newUser);
      setIsLoading(false);
      onClose();
    } catch (e: any) {
      setIsLoading(false);
      if (e.message.msg) {
        toast(e.message.msg, 'error');
      } else if (e.message) {
        toast(e.message, 'error');
      }
    }
  };

  const onClose = () => {
    user ? close() : clearValues();
  };

  async function init() {
    try {
      const config = await getConfiguration('CONF_PROCESSING_FEE_OVERRIDE');
      setProcessingFeeOverrideConf(config?.value as ProcessingFeeOverrideConf);
    } catch (e: any) {
      toast('Failed to fetch configuration', 'error');
    }
  }
  useEffect(() => {
    init().then(() => {
      if (user) {
        setName(user.name);
        setEmail(user.email);
        setCompany(user.company);
        setPhone(user.phone);
        setWithoutEmailChecked(false);
      }
    });
  }, [user]);

  useEffect(() => {
    setIsDisabled(name.length === 0 || (!withoutEmailChecked && email.length === 0));
  }, [name, withoutEmailChecked, email]);

  const buttonItems: { label: string; value: 'user' | 'company' }[] = [
    { label: 'User', value: 'user' },
    { label: 'Company', value: 'company' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      title={`${user ? 'Edit' : 'Add new'} ${userType == 'user' ? 'customer' : 'company'}`}
      actions={
        isLoading ? (
          <Spinner />
        ) : (
          <>
            <button className="button button-block button-secondary" data-cy="cancel_user" onClick={onClose}>
              Cancel
            </button>

            <button
              className="button button-yellow button-primary"
              data-cy="confirm_user"
              onClick={onSubmit}
              disabled={isDisabled}
            >
              {user ? 'Save' : 'Add'}
            </button>
          </>
        )
      }
      close={onClose}
    >
      <ButtonGroup<'user' | 'company'>
        items={buttonItems}
        onClick={(value) => setUserType(value as 'user' | 'company')}
      ></ButtonGroup>
      <Field
        label={`${userType == 'user' ? 'Full name' : 'Company name'}`}
        dataCy="full_name"
        type="text"
        value={name}
        onChange={setName}
        isRequired
      />

      {userType == 'user' && (
        <Field label="Company name" dataCy="company_name" type="text" value={company} onChange={setCompany} />
      )}

      <Phone
        label="Phone number"
        dataCy="phone_number"
        value={phone}
        onChange={setPhone}
        onChangeValidity={setPhoneIsValid}
      />

      <div className="flex flex-col gap-[6px]">
        <Field
          label="Email address"
          dataCy="email_address"
          type="text"
          value={email}
          onChange={setEmail}
          isRequired={!withoutEmailChecked}
        />

        {!user && (
          <Checkbox
            type="white"
            label={<span className="!text-grey-700">Does not have an email</span>}
            onChange={setWithoutEmailChecked}
            isChecked={withoutEmailChecked}
            dataCy="without_email"
          />
        )}
      </div>

      {!processingFeeOverrideConf ||
        (processingFeeOverrideConf !== ProcessingFeeOverrideConf.ApplyFee && (
          <Checkbox
            type="white"
            label={
              <span className="!text-grey-700">
                {processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                  ? 'Waive Processing Fee'
                  : 'Apply Processing Fee'}
              </span>
            }
            onChange={(isChecked) => {
              isChecked
                ? setProcessingFeeOverride(
                    processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                      ? ProcessingFeeOverride.WaiveFee
                      : ProcessingFeeOverride.ApplyFee,
                  )
                : setProcessingFeeOverride(undefined);
            }}
            isChecked={
              !processingFeeOverride
                ? false
                : processingFeeOverrideConf === ProcessingFeeOverrideConf.OptionallyApplyFee
                ? processingFeeOverride == ProcessingFeeOverride.WaiveFee
                : processingFeeOverride == ProcessingFeeOverride.ApplyFee
            }
            dataCy="processing_fee_override"
          />
        ))}

      {userType == 'company' && !user && (
        <>
          <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
            Primary Representative
          </Typography>
          <Field
            type="text"
            label="Full name"
            dataCy="full_name"
            value={representativeName}
            onChange={(name: string) => {
              setRepresentativeName(name);
            }}
            isRequired={true}
          />
          <Phone
            label="Phone number"
            dataCy="phone_number"
            value={phone}
            onChange={setRepresentativePhone}
            onChangeValidity={setRepresentativePhoneIsValid}
          />
          <Field
            type="text"
            label="Title"
            dataCy="representative_title"
            value={representativeTitle}
            onChange={(title: string) => {
              setRepresentativeTitle(title);
            }}
            isRequired={false}
          />

          <Field
            type="text"
            label="Email"
            value={representativeEmail}
            onChange={(email: string) => {
              setRepresentativeEmail(email);
            }}
            isRequired={false}
            dataCy="representative_email"
          />
        </>
      )}
    </Modal>
  );
};
